/**
 * staffApi — RTK Query slice for the mitarbeiter sub-feature.
 *
 * Replaces the plain-async `queries.ts` + `mutations.ts` adapter. Every RPC
 * in this feature goes through here; components and hooks use the generated
 * hooks (`useGetStaffPageBundleQuery`, `useUpdateStaffMutation`, etc).
 *
 * Mutation cache pattern (per `.agent/workflows/feature-restructure.md`):
 *  - **Pessimistic `onQueryStarted`** — patch `getStaffPageBundle` in place
 *    AFTER `queryFulfilled` succeeds. No optimistic updates; component
 *    state drives instant UI feedback.
 *  - **No `invalidatesTags`** — that would force a full refetch of the
 *    bundle after every save. We patch the list row in-place and let the
 *    caller force a per-row details refetch via `getStaffDetails`'s
 *    cache bump.
 *  - **Details are refetched explicitly** after mutations — callers
 *    dispatch `staffApi.endpoints.getStaffDetails.initiate({ staffId },
 *    { forceRefetch: true })` rather than tag-invalidating, so only the
 *    touched row goes over the wire.
 */
import { rootApi } from "../../../../lib/api/rootApi";
import type { StaffMember } from "../types";
import { weekDayLabelToInt } from "../utils";
import {
  rpcRowToStaffMember,
  mergeDetailsIntoMember,
  UI_ROLE_TO_DB,
} from "./mappers";
import type {
  DeactivateOptions,
  KioskChipRow,
  SendStaffInviteResult,
  StaffChangelogEntry,
  StaffDetailsRpcRow,
  StaffImportResultRow,
  StaffImportRowPayload,
  StaffListRpcRow,
  StaffPageBundle,
  StaffRoleOption,
  SubjectOption,
} from "./types";

// ============================================================================
// Helpers (local — not exported)
// ============================================================================

/** Shape of the jsonb return value from `fn_staff_create` / `fn_staff_update`. */
interface StaffMutationResult {
  success: boolean;
  staff_id?: string;
  school_id?: string;
  error?: string;
}

/** Single row from `generate_user_code_react`. */
interface GenerateUserCodeRow {
  code: string;
  expires_at: string | null;
}

/**
 * Build the shared payload shape that `fn_staff_create` and `fn_staff_update`
 * accept for the "optional staff fields". Both RPCs consume this structure
 * plus the id-specific field (`p_staff_id` on update).
 */
function buildStaffPayload(member: Omit<StaffMember, "id"> | StaffMember) {
  const ti = member.teacherInfo;
  const isLehrkraft = member.role === "Lehrkraft" && !!ti;

  const blockedTimes = Array.isArray(member.blockedTimes)
    ? member.blockedTimes.map((bt) => ({
        weekday: weekDayLabelToInt(bt.day),
        is_full_day: bt.isFullDay,
        start_time: bt.isFullDay ? null : bt.startTime || null,
        end_time: bt.isFullDay ? null : bt.endTime || null,
        type: bt.type || "blocked",
        reason: (bt.reason || "").trim() || null,
      }))
    : null;

  const subjectsStud = Array.isArray(ti?.studiedSubjects)
    ? ti!.studiedSubjects
    : null;

  return {
    p_first_name: member.firstName,
    p_last_name: member.lastName,
    p_status: member.status === "aktiv" ? "active" : "on_leave",
    p_role_name: UI_ROLE_TO_DB[member.role] || "Teacher",
    p_kurzung:
      typeof member.kurzung === "undefined"
        ? null
        : (member.kurzung || "").trim() || null,
    p_email: (member.email || "").trim() || null,
    p_phone: member.phone || null,
    p_tasks: member.tasks || null,
    p_hours_account:
      ti?.weeklyHours ?? member.educatorInfo?.weeklyHours ?? null,
    p_credit_hours: isLehrkraft ? (ti!.creditHours ?? 0) : null,
    p_age_reduction: isLehrkraft ? (ti!.ageReduction ?? 0) : null,
    p_credit_hours_note: isLehrkraft
      ? (ti!.creditHoursNote || "").trim() || null
      : null,
    p_subjects_stud: subjectsStud,
    p_permissions: Array.isArray(member.permissions) ? member.permissions : null,
    p_blocked_times: blockedTimes,
  };
}

/**
 * Patch the loaded `getStaffPageBundle` cache in-place after a mutation
 * succeeds. Matches the pattern documented in the feature-restructure
 * playbook (pessimistic cache update, no full refetch).
 *
 * Runs for every active bundle arg (there's only one in practice, but
 * RTK Query stores a separate entry per unique arg, so we iterate).
 */
function patchBundleCache(
  dispatch: any,
  getState: any,
  mutator: (draft: StaffPageBundle) => void,
) {
  const state = getState() as any;
  const entries = (state.api?.queries ?? {}) as Record<string, unknown>;
  for (const key of Object.keys(entries)) {
    if (!key.startsWith("getStaffPageBundle(")) continue;
    dispatch(
      staffApi.util.updateQueryData(
        "getStaffPageBundle",
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        undefined as any,
        (draft) => mutator(draft),
      ),
    );
  }
}

// ============================================================================
// Slice
// ============================================================================

export const staffApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    // ---- Queries ----------------------------------------------------------
    getStaffPageBundle: builder.query<StaffPageBundle, void>({
      queryFn: async (_arg, _queryApi, _extraOptions, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_staff_get_page_bundle",
          params: { p_include_external: false },
        });
        if (error) return { error };
        const payload = (data as {
          staff: StaffListRpcRow[];
          roles: StaffRoleOption[];
          subjects: SubjectOption[];
        } | null) || { staff: [], roles: [], subjects: [] };
        return {
          data: {
            staff: (payload.staff || []).map(rpcRowToStaffMember),
            roles: payload.roles || [],
            subjects: payload.subjects || [],
          },
        };
      },
      providesTags: [{ type: "Staff", id: "LIST" }],
      keepUnusedDataFor: 300,
    }),

    getStaffDetails: builder.query<StaffDetailsRpcRow | null, { staffId: string }>({
      queryFn: async ({ staffId }, _queryApi, _extraOptions, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_staff_get_details",
          params: { p_staff_id: staffId },
        });
        if (error) return { error };
        return { data: (data as StaffDetailsRpcRow | null) || null };
      },
      providesTags: (_res, _err, { staffId }) => [{ type: "Staff", id: staffId }],
      keepUnusedDataFor: 60,
    }),

    /**
     * Read-only metadata pre-fetched by `StaffImportDialog` on open —
     * primary roles, subject abbreviations, existing-staff index for
     * duplicate detection. Three plain table queries, not RPCs.
     */
    getStaffImportMetadata: builder.query<
      {
        primaryRoles: Array<{ id: string; name: string }>;
        subjectsAbbrev: string[];
        existingByKurzung: Record<
          string,
          { profile_id: string; first_name: string | null; last_name: string | null }
        >;
        existingByName: Record<
          string,
          { profile_id: string; first_name: string | null; last_name: string | null }
        >;
      },
      { schoolId: string }
    >({
      queryFn: async ({ schoolId }, _queryApi, _extraOptions, baseQuery) => {
        const rolesRes = await baseQuery({
          type: "select",
          table: "roles",
          params: {
            select: "id, name, is_subrole",
            is_subrole: false,
            name: ["Admin", "Erzieher*innen", "Teacher"],
          },
        });
        if (rolesRes.error) return { error: rolesRes.error };

        // Note: no `not is null` server-side filter here — the base
        // query's `is` operator only supports literal null/true/false, not
        // the PostgREST `not.is.null` form. Nulls are dropped client-side
        // via `.filter(Boolean)` below, which is effectively free since
        // subjects lists are small (a few dozen rows per school).
        const subjRes = await baseQuery({
          type: "select",
          table: "subjects",
          params: {
            select: "abbreviation",
            school_id: schoolId,
          },
        });
        if (subjRes.error) return { error: subjRes.error };

        const staffRes = await baseQuery({
          type: "select",
          table: "vw_react_staff_management",
          params: {
            select: "id, first_name, last_name, kurzung",
          },
        });
        if (staffRes.error) return { error: staffRes.error };

        const primaryRoles = ((rolesRes.data as Array<{ id: string; name: string }>) || []).map(
          (r) => ({ id: r.id, name: r.name }),
        );
        const subjectsAbbrev = (
          (subjRes.data as Array<{ abbreviation: string | null }>) || []
        )
          .map((s) => s.abbreviation || "")
          .filter(Boolean);

        const existingByKurzung: Record<
          string,
          { profile_id: string; first_name: string | null; last_name: string | null }
        > = {};
        const existingByName: Record<
          string,
          { profile_id: string; first_name: string | null; last_name: string | null }
        > = {};
        for (const s of (staffRes.data as Array<{
          id: string;
          first_name: string | null;
          last_name: string | null;
          kurzung: string | null;
        }>) || []) {
          if (s.kurzung) {
            existingByKurzung[String(s.kurzung).trim().toLowerCase()] = {
              profile_id: s.id,
              first_name: s.first_name,
              last_name: s.last_name,
            };
          }
          const key = `${(s.first_name || "").trim().toLowerCase()}|${(s.last_name || "").trim().toLowerCase()}`;
          existingByName[key] = {
            profile_id: s.id,
            first_name: s.first_name,
            last_name: s.last_name,
          };
        }

        return {
          data: { primaryRoles, subjectsAbbrev, existingByKurzung, existingByName },
        };
      },
      providesTags: [{ type: "Staff", id: "IMPORT_META" }],
      keepUnusedDataFor: 300,
    }),

    // ---- Create + Update --------------------------------------------------
    createStaff: builder.mutation<StaffMember, Omit<StaffMember, "id">>({
      queryFn: async (staffData, _queryApi, _extraOptions, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_staff_create",
          params: buildStaffPayload(staffData),
        });
        if (error) return { error };
        const result = (data as StaffMutationResult | null) || { success: false };
        if (!result.success || !result.staff_id) {
          return {
            error: {
              message: result.error || "fn_staff_create returned success=false",
              details: "",
              hint: "",
              code: "client_error",
            },
          };
        }
        return { data: { ...staffData, id: result.staff_id } as StaffMember };
      },
      async onQueryStarted(_arg, { dispatch, queryFulfilled, getState }) {
        try {
          const { data: created } = await queryFulfilled;
          patchBundleCache(dispatch, getState, (draft) => {
            // Append the new row so the list reflects the create without a
            // full bundle refetch. Details fields stay empty — the expanded
            // view will lazy-load via getStaffDetails on first click.
            draft.staff.push(created);
          });
          // Log the creation event so it appears in the changelog dialog.
          dispatch(
            staffApi.endpoints.logStaffEvent.initiate({
              profileId: created.id,
              actionType: "insert",
              details: { name: `${created.firstName} ${created.lastName}` },
            }),
          );
        } catch {
          // Component toast owns the error surface.
        }
      },
    }),

    updateStaff: builder.mutation<StaffMember, StaffMember>({
      queryFn: async (staffMember, _queryApi, _extraOptions, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_staff_update",
          params: {
            p_staff_id: staffMember.id,
            ...buildStaffPayload(staffMember),
          },
        });
        if (error) return { error };
        const result = (data as StaffMutationResult | null) || { success: false };
        if (!result.success) {
          return {
            error: {
              message: result.error || "fn_staff_update returned success=false",
              details: "",
              hint: "",
              code: "client_error",
            },
          };
        }
        return { data: staffMember };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled;
          patchBundleCache(dispatch, getState, (draft) => {
            const idx = draft.staff.findIndex((s) => s.id === arg.id);
            if (idx >= 0) {
              // Merge the draft edits on top of whatever's in the list — keep
              // the server-computed `detailsLoaded=false` so the caller's
              // forced getStaffDetails re-fetch fills in the heavy fields.
              draft.staff[idx] = { ...arg, detailsLoaded: false };
            }
          });
        } catch {
          // Component toast owns the error surface.
        }
      },
    }),

    // ---- Delete -----------------------------------------------------------
    deleteStaff: builder.mutation<void, { staffId: string }>({
      queryFn: async ({ staffId }, _queryApi, _extraOptions, baseQuery) => {
        const { error } = await baseQuery({
          type: "delete",
          table: "user_profiles",
          match: { id: staffId },
        });
        if (error) return { error };
        return { data: undefined };
      },
      async onQueryStarted({ staffId }, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled;
          patchBundleCache(dispatch, getState, (draft) => {
            draft.staff = draft.staff.filter((s) => s.id !== staffId);
          });
        } catch {
          // ignore
        }
      },
    }),

    // ---- Activate / deactivate --------------------------------------------
    activateStaff: builder.mutation<void, { staffId: string }>({
      queryFn: async ({ staffId }, _queryApi, _extraOptions, baseQuery) => {
        const { error } = await baseQuery({
          type: "rpc",
          rpc: "fn_staff_set_active",
          params: { p_staff_id: staffId, p_active: true },
        });
        if (error) return { error };
        return { data: undefined };
      },
      async onQueryStarted({ staffId }, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled;
          patchBundleCache(dispatch, getState, (draft) => {
            const idx = draft.staff.findIndex((s) => s.id === staffId);
            if (idx >= 0) {
              draft.staff[idx] = {
                ...draft.staff[idx],
                bannedUntil: null,
                detailsLoaded: false,
              };
            }
          });
        } catch {
          // ignore
        }
      },
    }),

    deactivateStaff: builder.mutation<
      void,
      { staffId: string; options: DeactivateOptions }
    >({
      queryFn: async (
        { staffId, options },
        _queryApi,
        _extraOptions,
        baseQuery,
      ) => {
        if (options.mode === "temporary" && !options.bannedUntil) {
          return {
            error: {
              message: "bannedUntil is required for temporary deactivation",
              details: "",
              hint: "",
              code: "client_error",
            },
          };
        }
        const bannedUntilIso =
          options.bannedUntil instanceof Date
            ? options.bannedUntil.toISOString()
            : options.bannedUntil ?? null;

        const { error } = await baseQuery({
          type: "rpc",
          rpc: "fn_staff_set_active",
          params: {
            p_staff_id: staffId,
            p_active: false,
            p_mode: options.mode,
            p_banned_until: bannedUntilIso,
          },
        });
        if (error) return { error };
        return { data: undefined };
      },
      async onQueryStarted(
        { staffId, options },
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          await queryFulfilled;
          patchBundleCache(dispatch, getState, (draft) => {
            const idx = draft.staff.findIndex((s) => s.id === staffId);
            if (idx < 0) return;
            if (options.mode === "permanent") {
              // Permanent revoke: auth row deleted → has_login goes false,
              // bannedUntil cleared, details must reload.
              draft.staff[idx] = {
                ...draft.staff[idx],
                accountStatus: "none",
                bannedUntil: null,
                detailsLoaded: false,
              };
            } else {
              const iso =
                options.bannedUntil instanceof Date
                  ? options.bannedUntil.toISOString()
                  : options.bannedUntil ?? null;
              draft.staff[idx] = {
                ...draft.staff[idx],
                bannedUntil: iso,
                detailsLoaded: false,
              };
            }
          });
        } catch {
          // ignore — dialog handles inline error state
        }
      },
    }),

    // ---- Auxiliary: invite email, password reset, excel import ------------
    sendStaffInviteEmail: builder.mutation<SendStaffInviteResult, StaffMember>({
      queryFn: async (member, _queryApi, _extraOptions, baseQuery) => {
        if (!member.email) {
          return {
            error: {
              message: "Staff member has no email address.",
              details: "",
              hint: "",
              code: "client_error",
            },
          };
        }
        // 1. Generate a new access code
        const codeRes = await baseQuery({
          type: "rpc",
          rpc: "generate_user_code_react",
          params: {
            p_profile_id: member.id,
            p_code_instance: 1,
            p_created_by: null,
            p_notes: `Email invitation for ${member.firstName} ${member.lastName}`,
          },
        });
        if (codeRes.error) return { error: codeRes.error };
        const codeResult = (Array.isArray(codeRes.data)
          ? codeRes.data[0]
          : codeRes.data) as GenerateUserCodeRow | null;
        if (!codeResult?.code) {
          return {
            error: {
              message: "No access code returned from generate_user_code_react",
              details: "",
              hint: "",
              code: "client_error",
            },
          };
        }

        // 2. Fire the edge function. The base query doesn't speak edge
        // functions; use the Supabase client directly here — this is the
        // one place it's allowed because there's no table/rpc equivalent.
        const fullName = `${member.firstName} ${member.lastName}`;
        const deepLink = `${window.location.origin}/auth/external-signup?code=${encodeURIComponent(codeResult.code)}&name=${encodeURIComponent(fullName)}`;
        const { supabase } = await import("../../../../lib/supabase");
        const { error: emailError } = await supabase.functions.invoke(
          "send-external-invitation",
          {
            body: {
              recipientType: "staff" as const,
              recipientEmail: member.email,
              recipientName: fullName,
              accessCode: codeResult.code,
              schoolName: "Ihre Schule", // TODO: thread through from bundle
              expirationDate: codeResult.expires_at
                ? new Date(codeResult.expires_at).toLocaleDateString("de-DE")
                : undefined,
              loginUrl: deepLink,
            },
          },
        );
        if (emailError) {
          // supabase-js only exposes a generic "non-2xx" string on .message —
          // the real error text from the edge function lives on the underlying
          // Response in .context. Pull it so rate-limit / permission messages
          // actually reach the user.
          let detail = emailError.message || String(emailError);
          try {
            const resp = (emailError as { context?: Response }).context;
            if (resp && typeof resp.json === "function") {
              const body = await resp.json();
              if (body?.error) detail = body.error;
            }
          } catch {
            // keep the default message
          }
          return {
            error: {
              message: `Failed to send email: ${detail}`,
              details: "",
              hint: "",
              code: "edge_function_error",
            },
          };
        }
        return { data: { code: codeResult.code } };
      },
      async onQueryStarted(member, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            staffApi.endpoints.logStaffEvent.initiate({
              profileId: member.id,
              actionType: "invite_sent",
              details: { email: member.email ?? null, name: `${member.firstName} ${member.lastName}` },
            }),
          );
        } catch {
          // logging is best-effort
        }
      },
    }),

    sendStaffPasswordReset: builder.mutation<void, { authEmail: string; staffId?: string }>({
      queryFn: async ({ authEmail }) => {
        if (!authEmail) {
          return {
            error: {
              message: "Auth email required for password reset",
              details: "",
              hint: "",
              code: "client_error",
            },
          };
        }
        // supabase.auth isn't a PostgREST call — direct client access, same
        // rationale as the edge function above.
        const { supabase } = await import("../../../../lib/supabase");
        const { error } = await supabase.auth.resetPasswordForEmail(authEmail, {
          redirectTo: `${window.location.origin}/auth/reset-password`,
        });
        if (error) {
          return {
            error: {
              message: error.message,
              details: "",
              hint: "",
              code: "auth_error",
            },
          };
        }
        return { data: undefined };
      },
      async onQueryStarted({ staffId, authEmail }, { dispatch, queryFulfilled }) {
        if (!staffId) return;
        try {
          await queryFulfilled;
          dispatch(
            staffApi.endpoints.logStaffEvent.initiate({
              profileId: staffId,
              actionType: "password_reset_sent",
              details: { email: authEmail },
            }),
          );
        } catch {
          // logging is best-effort
        }
      },
    }),

    importStaffExcel: builder.mutation<
      StaffImportResultRow[],
      {
        rows: StaffImportRowPayload[];
        overwriteBlanks: boolean;
        globalDupStrategy: string;
      }
    >({
      queryFn: async (args, _queryApi, _extraOptions, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_staff_import_excel",
          params: {
            p_rows: args.rows,
            p_overwrite_blanks: args.overwriteBlanks,
            p_global_dup_strategy: args.globalDupStrategy,
          },
        });
        if (error) return { error };
        return {
          data: (Array.isArray(data) ? data : []) as StaffImportResultRow[],
        };
      },
      // Invalidates the bundle — Excel import can create, update, or skip
      // dozens of rows, so an in-place patch is impractical. Let the next
      // consumer re-fetch the list.
      invalidatesTags: [{ type: "Staff", id: "LIST" }],
    }),

    // ---- Kiosk chip management --------------------------------------------

    /** All active chips for the school. Filtered per-user in the component. */
    listKioskChips: builder.query<KioskChipRow[], void>({
      queryFn: async (_arg, _queryApi, _extraOptions, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_kiosk_chip_admin_list",
          params: { p_include_revoked: false },
        });
        if (error) return { error };
        return { data: (Array.isArray(data) ? data : []) as KioskChipRow[] };
      },
      keepUnusedDataFor: 120,
    }),

    enrollKioskChip: builder.mutation<
      { ok: boolean; chip_id?: string; error?: string },
      { userId: string; uid: string; chipLabel?: string | null }
    >({
      queryFn: async ({ userId, uid, chipLabel }, _queryApi, _extraOptions, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_kiosk_chip_admin_enrol",
          params: { p_user_id: userId, p_uid: uid, p_chip_label: chipLabel ?? null },
        });
        if (error) return { error };
        return { data: data as { ok: boolean; chip_id?: string; error?: string } };
      },
      async onQueryStarted({ userId, uid, chipLabel }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (!data.ok || !data.chip_id) return;
          const normalised = uid.toUpperCase().replace(/[^0-9A-F]/gi, "");
          dispatch(
            staffApi.util.updateQueryData("listKioskChips", undefined, (draft) => {
              draft.push({
                id: data.chip_id!,
                user_id: userId,
                user_first_name: null,
                user_last_name: null,
                chip_uid_hex: normalised,
                chip_label: chipLabel ?? null,
                enrolled_at: new Date().toISOString(),
                enrolled_by: null,
                revoked_at: null,
                revoked_by: null,
                revoke_reason: null,
                is_active: true,
              });
            }),
          );
        } catch {
          // component surfaces error
        }
      },
    }),

    revokeKioskChip: builder.mutation<
      { ok: boolean; error?: string },
      { chipId: string; reason?: string | null }
    >({
      queryFn: async ({ chipId, reason }, _queryApi, _extraOptions, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_kiosk_chip_admin_revoke",
          params: { p_chip_id: chipId, p_reason: reason ?? null },
        });
        if (error) return { error };
        return { data: data as { ok: boolean; error?: string } };
      },
      async onQueryStarted({ chipId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            staffApi.util.updateQueryData("listKioskChips", undefined, (draft) => {
              const idx = draft.findIndex((c) => c.id === chipId);
              if (idx >= 0) draft.splice(idx, 1);
            }),
          );
        } catch {
          // component surfaces error
        }
      },
    }),

    // ---- Changelog --------------------------------------------------------
    getStaffChangelog: builder.query<StaffChangelogEntry[], { staffId: string }>({
      queryFn: async ({ staffId }, _queryApi, _extraOptions, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_staff_get_changelog",
          params: { p_profile_id: staffId, p_limit: 100, p_offset: 0 },
        });
        if (error) return { error };
        return { data: (Array.isArray(data) ? data : []) as StaffChangelogEntry[] };
      },
    }),

    logStaffEvent: builder.mutation<
      void,
      { profileId: string; actionType: string; details?: Record<string, unknown> | null }
    >({
      queryFn: async (args, _queryApi, _extraOptions, baseQuery) => {
        const { error } = await baseQuery({
          type: "rpc",
          rpc: "fn_staff_log_event",
          params: {
            p_profile_id:  args.profileId,
            p_action_type: args.actionType,
            p_details:     args.details ?? null,
          },
        });
        if (error) return { error };
        return { data: undefined };
      },
    }),
  }),
});

// Export hook bindings
export const {
  useGetStaffPageBundleQuery,
  useGetStaffDetailsQuery,
  useLazyGetStaffDetailsQuery,
  useGetStaffImportMetadataQuery,
  useCreateStaffMutation,
  useUpdateStaffMutation,
  useDeleteStaffMutation,
  useActivateStaffMutation,
  useDeactivateStaffMutation,
  useSendStaffInviteEmailMutation,
  useSendStaffPasswordResetMutation,
  useImportStaffExcelMutation,
  useGetStaffChangelogQuery,
  useLogStaffEventMutation,
  useListKioskChipsQuery,
  useEnrollKioskChipMutation,
  useRevokeKioskChipMutation,
} = staffApi;

// Helper — force-refetch a single row's details. Used by mutations after
// save/activate so the expanded UI reflects server state without
// refetching the whole bundle.
export function refreshStaffDetails(dispatch: any, staffId: string) {
  dispatch(
    staffApi.endpoints.getStaffDetails.initiate(
      { staffId },
      { forceRefetch: true, subscribe: false },
    ),
  );
}

// Re-export the merge helper so consumers don't need to reach into mappers.ts.
export { mergeDetailsIntoMember };
